import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Clock, AlertTriangle, CalendarOff, Check, CheckCheck, ClipboardList, MessageSquare, UserCheck, FileCheck, Calendar, Send, XCircle, FileWarning, X, Trash2, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

const NOTIF_CONFIG = {
  late_arrival: { icon: Clock, color: 'text-orange-500', bg: 'bg-orange-50', label: 'Retard', route: '/admin/history' },
  unjustified_absence: { icon: CalendarOff, color: 'text-red-500', bg: 'bg-red-50', label: 'Absence injustifiee', route: '/admin/absences' },
  out_of_schedule: { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Hors horaires', route: '/admin/history' },
  daily_recap: { icon: ClipboardList, color: 'text-brand-blue', bg: 'bg-blue-50', label: 'Recap du jour', route: '/admin/dashboard' },
  month_validated_by_admin: { icon: CheckCheck, color: 'text-green-600', bg: 'bg-green-50', label: 'Validation mensuelle', route: '/admin/stats' },
  regularization_request: { icon: FileCheck, color: 'text-purple-500', bg: 'bg-purple-50', label: 'Demande de regularisation', route: '/admin/history' },
  regularization_response: { icon: FileCheck, color: 'text-purple-600', bg: 'bg-purple-50', label: 'Reponse regularisation', route: '/admin/history' },
  employee_message: { icon: MessageSquare, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Message', route: '/admin/messages' },
  leave_request: { icon: Calendar, color: 'text-teal-600', bg: 'bg-teal-50', label: 'Demande de conge', route: '/admin/absences' },
  leave_approved: { icon: Check, color: 'text-green-600', bg: 'bg-green-50', label: 'Conge approuve', route: '/admin/absences' },
  leave_rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'Conge refuse', route: '/admin/absences' },
  contract_expiry: { icon: FileWarning, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Expiration contrat', route: '/admin/employees' },
  cost_alert: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', label: 'Alerte cout', route: '/admin/costs' },
  system_crash: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', label: 'Alerte systeme', route: '/admin/dashboard' },
  password_reset: { icon: FileCheck, color: 'text-orange-500', bg: 'bg-orange-50', label: 'Reinitialisation mdp', route: '/admin/dashboard' },
  subscription_expiry: { icon: Gift, color: 'text-green-600', bg: 'bg-green-50', label: 'Fin abonnement offert', route: '/admin/subscription' },
  cash_closure: { icon: ClipboardList, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Cloture caisse', route: '/admin/caisse' },
  cash_daily_closure: { icon: ClipboardList, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Clôture journalière', route: '/admin/caisse' },
  merch_sent: { icon: ClipboardList, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Marchandise à réceptionner', route: '/admin/merchandise' },
  merch_received: { icon: CheckCheck, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Contrôle de réception', route: '/admin/merchandise' },
  cash_deposit_anomaly: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', label: 'Anomalie depot caisse', route: '/admin/caisse' },
  cash_fund_anomaly: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50', label: 'Anomalie fond de caisse', route: '/admin/caisse' },
  _default: { icon: Bell, color: 'text-gray-500', bg: 'bg-gray-50', label: 'Notification', route: '/admin/dashboard' }
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [photoZoom, setPhotoZoom] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) {
      fetchNotifications();
      // Marquer tout comme lu dès l'ouverture du panel
      if (unreadCount > 0) {
        markAllRead();
      }
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(res.data.count);
    } catch (err) {
      // silent
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notifications`, {
        params: { limit: 30 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notifId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/notifications/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification:', err);
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all read:', err);
    }
  };

  const deleteNotification = async (notifId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/notifications/${notifId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleNotifClick = (notif) => {
    if (!notif.is_read) markAsRead(notif.id);
    const config = NOTIF_CONFIG[notif.notification_type] || NOTIF_CONFIG._default;
    const route = (['merch_sent', 'merch_received', 'merch_delivered'].includes(notif.notification_type) && /^\/admin\/merchandise\?/.test(notif.url || '')) || /^\/admin\/transport/.test(notif.url || '') ? notif.url : config.route;
    if (route) {
      setOpen(false);
      navigate(route);
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    // Ensure UTC interpretation: add Z if no timezone indicator
    const utcStr = isoStr.endsWith('Z') || isoStr.includes('+') || isoStr.includes('-', 10) ? isoStr : isoStr + 'Z';
    const d = new Date(utcStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "A l'instant";
    if (diffMin < 60) return `Il y a ${diffMin} min`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `Il y a ${diffH}h`;
    const diffD = Math.floor(diffH / 24);
    return `Il y a ${diffD}j`;
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="fixed sm:absolute left-2 right-2 top-16 sm:left-auto sm:right-auto sm:top-12 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50"
            data-testid="notification-panel"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <h3 className="font-heading text-sm text-brand-blue">Notifications</h3>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllRead}
                  className="text-xs text-brand-blue hover:bg-brand-blue/10 h-7 px-2"
                  data-testid="mark-all-read-button"
                >
                  <CheckCheck className="w-3 h-3 mr-1" />
                  Tout lire
                </Button>
              )}
            </div>

            {/* Notifications list */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-blue"></div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm font-body">Aucune notification</p>
                </div>
              ) : (
                notifications.map((notif) => {
                  const config = NOTIF_CONFIG[notif.notification_type] || NOTIF_CONFIG._default;
                  const Icon = config.icon;
                  return (
                    <div
                      key={notif.id}
                      className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                        !notif.is_read ? 'bg-blue-50/40' : ''
                      }`}
                      data-testid={`notification-item-${notif.id}`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                          <Icon className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <div
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => handleNotifClick(notif)}
                        >
                          <div className="flex items-center space-x-2">
                            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${config.bg} ${config.color}`}>
                              {config.label}
                            </span>
                            {!notif.is_read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-sm text-gray-800 mt-1 font-body leading-tight">
                            {notif.notification_type === 'daily_recap' ? (
                              <span className="space-y-0.5 block">
                                {notif.message.split(' | ').map((line, i) => (
                                  <span key={i} className={`block ${i === 0 ? 'font-semibold text-brand-blue' : ''}`}>
                                    {line}
                                  </span>
                                ))}
                              </span>
                            ) : notif.message}
                          </p>
                          {notif.photo_url && (
                            <div className="mt-2">
                              {notif.photo_url.match(/\.(webm|mp4|ogg)/) ? (
                                <video
                                  src={`${BACKEND_URL}${notif.photo_url}`}
                                  className="w-16 h-16 rounded-lg object-cover cursor-pointer border border-gray-200 hover:border-blue-400 transition-colors"
                                  onClick={(e) => { e.stopPropagation(); setPhotoZoom(`${BACKEND_URL}${notif.photo_url}`); }}
                                  muted autoPlay loop playsInline
                                />
                              ) : (
                                <img
                                  src={`${BACKEND_URL}${notif.photo_url}`}
                                  alt="Capture pointage"
                                  className="w-16 h-16 rounded-lg object-cover cursor-pointer border border-gray-200 hover:border-blue-400 transition-colors"
                                  onClick={(e) => { e.stopPropagation(); setPhotoZoom(`${BACKEND_URL}${notif.photo_url}`); }}
                                />
                              )}
                            </div>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTime(notif.created_at)}
                          </p>
                        </div>
                        <button
                          onClick={() => deleteNotification(notif.id)}
                          className="p-1.5 rounded-md hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                          title="Supprimer"
                          data-testid={`delete-notif-${notif.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Capture zoom modal */}
      {photoZoom && (
        <div
          className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4"
          onClick={() => setPhotoZoom(null)}
        >
          {photoZoom.match(/\.(webm|mp4|ogg)/) ? (
            <video src={photoZoom} controls autoPlay loop muted className="max-h-[80vh] max-w-[90vw] rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()} />
          ) : (
            <img src={photoZoom} alt="Capture pointage" className="max-h-[80vh] max-w-[90vw] rounded-xl shadow-2xl" />
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
